/**
 * Referrals page — share link, free-day rewards, invitee list.
 *
 * Route: /app/referrals
 */

import { motion } from "framer-motion";
import {
  ArrowLeft,
  CalendarPlus,
  Check,
  Copy,
  Crown,
  Gift,
  Send,
  Share2,
  Sparkles,
  Users,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Button, Card } from "@/design";
import { useReferralList, useReferralStats } from "@/api/referrals";
import { cn } from "@/lib/cn";
import { haptic, tg } from "@/lib/telegram";

const PLAN_TITLES: Record<string, string> = {
  econom: "Эконом",
  standard: "Стандарт",
  quarter: "Квартал",
  godbless: "GodBless",
};
const PLAN_ORDER = ["econom", "standard", "quarter", "godbless"];

export function ReferralsPage() {
  const navigate = useNavigate();
  const { data: stats } = useReferralStats();
  const { data: list } = useReferralList({ limit: 30 });
  const [copied, setCopied] = useState(false);

  const rewardDaysByPlan = stats?.reward_days_by_plan ?? {};

  const handleCopy = async () => {
    if (!stats?.deep_link) return;
    haptic.select();
    try {
      await navigator.clipboard.writeText(stats.deep_link);
      setCopied(true);
      toast.success("Ссылка скопирована");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Не удалось скопировать");
    }
  };

  const handleShare = () => {
    if (!stats?.deep_link) return;
    haptic.impact("medium");
    const text =
      "🚀 Подключайся к SilentPoster — авто-постинг в Telegram-чаты\n" +
      "Сервис помогает держать рассылку в работе без ручной рутины.";
    const url = `https://t.me/share/url?url=${encodeURIComponent(stats.deep_link)}&text=${encodeURIComponent(text)}`;
    if (tg) tg.openTelegramLink(url);
    else window.open(url, "_blank", "noopener");
  };

  return (
    <div className="px-5 pt-4 pb-safe-nav space-y-5">
      <div className="flex items-center justify-between pt-1">
        <button
          onClick={() => {
            haptic.select();
            navigate(-1);
          }}
          className="size-10 rounded-full glass grid place-items-center hover:bg-white/[0.06] transition-colors"
          aria-label="Назад"
        >
          <ArrowLeft className="size-4" />
        </button>
        <div className="text-[15px] font-semibold tracking-tight">Реферальная программа</div>
        <button
          onClick={handleShare}
          className="size-10 rounded-full glass grid place-items-center hover:bg-white/[0.06] transition-colors"
          aria-label="Поделиться"
        >
          <Share2 className="size-4" />
        </button>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      >
        <Card padded={false} className="relative overflow-hidden">
          <div
            className="absolute inset-0 bg-gradient-to-br from-accent/25 via-transparent to-transparent pointer-events-none"
            aria-hidden
          />
          <div className="relative p-6 space-y-5">
            <div className="flex items-center gap-2">
              <div className="size-9 rounded-xl bg-gradient-accent grid place-items-center shadow-[0_8px_32px_-8px_rgba(255,69,0,0.6)]">
                <Sparkles className="size-4 text-white" />
              </div>
              <div className="text-xs text-text-muted uppercase tracking-wider font-medium">
                Бесплатные дни
              </div>
            </div>

            <div className="flex items-baseline gap-2">
              <span className="text-[42px] leading-none font-semibold tabular-nums tracking-tight">
                {stats?.total_reward_days ?? 0}
              </span>
              <span className="text-text-muted text-sm">{pluralDays(stats?.total_reward_days ?? 0)}</span>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <MiniStat icon={<Users className="size-3" />} label="Приглашено" value={stats?.referrals_count ?? 0} />
              <MiniStat icon={<Crown className="size-3" />} label="Оплатили" value={stats?.paid_referrals_count ?? 0} />
              <MiniStat icon={<CalendarPlus className="size-3" />} label="За Стандарт" value={`+${rewardDaysByPlan.standard ?? 14}`} />
            </div>

            <Button fullWidth variant="primary" leftIcon={<Send />} onClick={handleShare}>
              Поделиться ссылкой
            </Button>
          </div>
        </Card>
      </motion.div>

      <Card padded>
        <div className="text-xs text-text-muted uppercase tracking-wider font-medium mb-2">
          Ваша ссылка
        </div>
        <div className="rounded-xl bg-white/[0.04] border border-white/[0.05] px-3 py-2.5 text-[13px] font-mono truncate">
          {stats?.deep_link ?? "Загрузка…"}
        </div>
        <div className="grid grid-cols-2 gap-2 mt-3">
          <Button variant="secondary" leftIcon={copied ? <Check /> : <Copy />} onClick={handleCopy}>
            {copied ? "Скопировано" : "Копировать"}
          </Button>
          <Button leftIcon={<Send />} onClick={handleShare}>
            Поделиться
          </Button>
        </div>
        <div className="mt-3 text-[11px] text-text-muted leading-relaxed">
          За каждую оплату приглашённого вы получаете бесплатные дни подписки.
          Регистрация, триал и промокоды не начисляют бонус — только реальная оплата.
        </div>
      </Card>

      <div className="space-y-2">
        <div className="text-xs text-text-muted uppercase tracking-wider font-medium px-1 flex items-center gap-1.5">
          <Gift className="size-3" />
          Сколько начисляем
        </div>
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] divide-y divide-white/[0.04] overflow-hidden">
          {PLAN_ORDER.map((planId) => (
            <RewardRow key={planId} planId={planId} days={rewardDaysByPlan[planId] ?? 0} />
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <div className="text-xs text-text-muted uppercase tracking-wider font-medium px-1 flex items-center gap-1.5">
          <Users className="size-3" />
          Ваши приглашённые
        </div>
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] divide-y divide-white/[0.04] overflow-hidden">
          {!list || list.items.length === 0 ? (
            <div className="px-4 py-10 text-center text-xs text-text-muted">
              Пока никого нет — поделитесь ссылкой
            </div>
          ) : (
            list.items.map((r) => (
              <div key={r.id} className="px-4 py-3 flex items-center gap-3">
                <div
                  className={cn(
                    "size-9 rounded-xl grid place-items-center shrink-0 text-[13px] font-semibold",
                    r.has_paid ? "bg-accent-soft text-accent" : "bg-white/[0.04] text-text-dim",
                  )}
                >
                  {(r.first_name ?? r.username ?? "?").charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium truncate">
                    {r.first_name ?? r.username ?? `User #${r.id}`}
                  </div>
                  <div className="text-[11px] text-text-muted">
                    {r.has_paid ? "Оплатил подписку — ждите бонус" : "Зарегистрировался"}
                  </div>
                </div>
                {r.has_paid && (
                  <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-success/15 text-success text-[10px] font-medium">
                    <Crown className="size-3" />
                    Paid
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function MiniStat({ icon, label, value }: { icon: ReactNode; label: string; value: string | number }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-2.5">
      <div className="flex items-center gap-1 text-text-dim">{icon}</div>
      <div className="text-[10px] text-text-dim uppercase tracking-wider mt-1">{label}</div>
      <div className="text-[15px] font-semibold tabular-nums mt-0.5">{value}</div>
    </div>
  );
}

function RewardRow({ planId, days }: { planId: string; days: number }) {
  return (
    <div className="px-4 py-3 flex items-center gap-3">
      <div className="size-9 rounded-xl bg-accent-soft text-accent grid place-items-center shrink-0">
        <CalendarPlus className="size-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium truncate">Реферал оплатил {PLAN_TITLES[planId] ?? planId}</div>
        <div className="text-[11px] text-text-muted">Начислим к вашей подписке</div>
      </div>
      <div className="text-[13px] font-semibold tabular-nums text-accent">+{days} дн.</div>
    </div>
  );
}

function pluralDays(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return "день";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return "дня";
  return "дней";
}
